import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import Icon from 'components/blocks/Icon/Icon';
import { configureStreetViewPanorama, getStreetViewMapOptions } from 'utils/streetView';

interface GeoMarker {
  center: any;
  lat: number;
  lng: number;
}

const MAP_HEIGHT = 400;
const SPAIN_CENTER = { lat: 40.4168, lng: -3.7038 };
const SPAIN_ZOOM = 6;

interface Props {
  /** Full list for geocoding (changes only on province switch) */
  allCenters: any[];
  /** Filtered subset to show as markers */
  visibleCenters: any[];
}

const CentersMap: React.FC<Props> = ({ allCenters, visibleCenters }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_REACT_APP_GOOGLE_MAPS_API_KEY || '',
    language: 'es',
    region: 'ES',
    version: 'weekly',
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  // Keyed by center.Id → {lat, lng}
  const geocodeCache = useRef<Record<string, { lat: number; lng: number }>>({});
  const [markers, setMarkers] = useState<GeoMarker[]>([]);
  const [geocoding, setGeocoding] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Geocode the full allCenters list when it changes (province switch)
  useEffect(() => {
    mapRef.current?.getStreetView().setVisible(false);
    setActiveId(null);

    if (!isLoaded || allCenters.length === 0) {
      setMarkers([]);
      return;
    }

    setGeocoding(true);
    const geocoder = new window.google.maps.Geocoder();

    const tasks = allCenters.map((center) => {
      const cacheKey = String(center.Id);

      if (geocodeCache.current[cacheKey]) {
        return Promise.resolve<GeoMarker>({
          center,
          ...geocodeCache.current[cacheKey],
        });
      }

      const address = [
        center.TipoViaDescripcion,
        center.NombreCalle,
        center.NumeroCalle,
        center.LocalidadDescripcion,
        'España',
      ]
        .filter(Boolean)
        .join(' ');

      return new Promise<GeoMarker | null>((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const pos = {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            };
            geocodeCache.current[cacheKey] = pos;
            resolve({ center, ...pos });
          } else {
            resolve(null);
          }
        });
      });
    });

    Promise.all(tasks).then((results) => {
      setMarkers(results.filter((r): r is GeoMarker => r !== null));
      setGeocoding(false);
    });
  }, [isLoaded, allCenters]);

  // Fit map bounds to visible markers
  const visibleIds = new Set(visibleCenters.map((c) => String(c.Id)));
  const shownMarkers = markers.filter((m) => visibleIds.has(String(m.center.Id)));

  useEffect(() => {
    if (!mapRef.current || shownMarkers.length === 0) return;

    if (shownMarkers.length === 1) {
      mapRef.current.setCenter({ lat: shownMarkers[0].lat, lng: shownMarkers[0].lng });
      mapRef.current.setZoom(14);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    shownMarkers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    mapRef.current.fitBounds(bounds, 60);
  }, [shownMarkers.length, markers]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    configureStreetViewPanorama(map);
  }, []);

  const activeMarker = activeId ? shownMarkers.find((m) => String(m.center.Id) === activeId) : null;

  if (!isLoaded) {
    return (
      <div className="centers-map">
        <div className="centers-map__skeleton">
          <div className="centers-map__skeleton-inner">
            <div className="centers-map__spinner" />
            <span className="centers-map__loading-text">Cargando mapa…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="centers-map-wrapper">
      <div className="centers-map__header">
        <h3 className="centers-map__title">Mapa de centros</h3>
        <button
          type="button"
          className="centers-map__expand-button"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-pressed={isExpanded}
        >
          {isExpanded ? 'Reducir mapa' : 'Ampliar mapa'}
        </button>
      </div>
      <div className="centers-map">
        {geocoding && (
          <div className="centers-map__overlay">
            <span className="centers-map__loading-text">Cargando mapa…</span>
          </div>
        )}
        <GoogleMap
          mapContainerStyle={{
            width: '100%',
            height: `${isExpanded ? MAP_HEIGHT * 2 : MAP_HEIGHT}px`,
          }}
          center={SPAIN_CENTER}
          zoom={SPAIN_ZOOM}
          onLoad={onMapLoad}
          options={{
            ...getStreetViewMapOptions(),
            mapTypeControl: false,
            fullscreenControl: true,
            zoomControl: true,
          }}
        >
          {shownMarkers.map((m) => (
            <Marker
              key={m.center.Id}
              position={{ lat: m.lat, lng: m.lng }}
              title={m.center.Descripcion}
              onClick={() => setActiveId(String(m.center.Id))}
            />
          ))}

          {activeMarker && (
            <InfoWindow
              position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
              onCloseClick={() => setActiveId(null)}
              options={{ maxWidth: 260 }}
            >
              <div className="centers-map__info">
                <p className="centers-map__info-name">{activeMarker.center.Descripcion}</p>
                <p className="centers-map__info-addr">
                  {`${activeMarker.center.TipoViaDescripcion ?? ''} ${activeMarker.center.NombreCalle ?? ''} ${activeMarker.center.NumeroCalle ?? ''}, ${activeMarker.center.LocalidadDescripcion ?? ''}`.trim()}
                </p>
                {activeMarker.center.Telefono && (
                  <a
                    href={`tel:${activeMarker.center.Telefono}`}
                    className="centers-map__info-phone"
                  >
                    {activeMarker.center.Telefono}
                  </a>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default CentersMap;
